// v19.29 — Public beat store catalog + search.
// Route: /beats
// Design: dark card style matching the rest of C.R.O.W., mobile-first
// grid, prominent YouTube-facing search bar at the top.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, Play, Pause, MusicNote, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import API from "../api";
import GlobalNav from "../components/GlobalNav";
import ProfileCircle from "../auth/ProfileCircle";

const priceFmt = (cents) => `$${((cents || 0) / 100).toFixed(0)}`;

export default function BeatsPage() {
  const nav = useNavigate();
  const [query, setQuery] = useState("");
  const [beats, setBeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  // Track a page_view for the whole store landing so Phase C analytics
  // can compute funnel numbers even before purchases exist.
  useEffect(() => {
    try {
      const src = new URLSearchParams(window.location.search).get("utm_source") || "direct";
      API.post("/store/track", { event: "page_view", path: "/beats", utm_source: src }).catch(() => {});
    } catch { /* noop */ }
  }, []);

  const load = async (q) => {
    setLoading(true);
    try {
      const { data } = await API.get(`/store/beats${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setBeats(data.beats || []);
    } catch (e) {
      toast.error("Could not load beats");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(""); }, []);

  const onSearch = async () => {
    const q = query.trim();
    if (!q) { load(""); return; }
    // Exact-name jump: try lookup first; if exactly 1 match, navigate
    // straight to that beat's page (YouTube video → direct page).
    try {
      const { data } = await API.get(`/store/beats/lookup?name=${encodeURIComponent(q)}`);
      if ((data.beats || []).length === 1) {
        const b = data.beats[0];
        nav(`/beats/${b.artist_slug}/${b.name_slug}`);
        return;
      }
      // Otherwise fall through to a partial search-result list.
      load(q);
    } catch {
      load(q);
    }
  };

  const previewToggle = (b) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}${b.preview_stream_url}`;
    if (!audioRef.current) audioRef.current = new Audio();
    const a = audioRef.current;
    if (playingId === b.id) {
      a.pause();
      setPlayingId(null);
      return;
    }
    a.src = url;
    a.play().then(() => setPlayingId(b.id)).catch(() => toast.error("Preview unavailable"));
    a.onended = () => setPlayingId(null);
  };

  return (
    <div className="min-h-screen text-white" style={{ background: "radial-gradient(circle at 50% 0%, rgba(34,197,94,0.05), #000 60%)" }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center gap-2 sm:gap-3 mb-6">
          <button onClick={() => nav("/")} className="text-white/50 hover:text-white text-sm flex items-center gap-1 whitespace-nowrap" data-testid="store-back-home">
            <ArrowLeft size={16} weight="bold" /> Home
          </button>
          <GlobalNav />
          <div className="flex-1" />
          <button onClick={() => nav("/")} className="hidden sm:block text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-emerald-400/40 hover:text-emerald-300 whitespace-nowrap" data-testid="store-open-generator">Beat Generator</button>
          <ProfileCircle signInPill />
        </div>
        <h1 className="font-display font-bold text-4xl sm:text-5xl mb-2 tracking-tight">Beat Store</h1>
        <p className="text-white/60 text-sm sm:text-base mb-6 max-w-xl">Every beat you hear on YouTube — instantly. Search by name to jump straight to the one from the video.</p>

        <div className="relative mb-8">
          <div className="flex items-center gap-2 rounded-2xl bg-[#0a0a0a] border border-[#1f1f1f] focus-within:border-emerald-400/60 px-3 py-3 sm:px-5 sm:py-4 transition">
            <MagnifyingGlass size={22} weight="bold" className="text-white/40 shrink-0" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") onSearch(); }}
              placeholder='Heard a beat on YouTube? Type its name here.'
              className="w-full bg-transparent outline-none text-sm sm:text-base placeholder:text-white/30"
              data-testid="store-search-input"
              autoFocus
            />
            <button onClick={onSearch} className="hidden sm:inline-flex px-4 py-2 rounded-full text-sm font-bold bg-emerald-500 text-black hover:bg-emerald-400" data-testid="store-search-btn">Search</button>
          </div>
        </div>

        {loading ? (
          <div className="text-white/40 text-sm">Loading beats…</div>
        ) : beats.length === 0 ? (
          <div className="text-white/60 text-sm border border-dashed border-white/10 rounded-xl p-8 text-center">
            No beats match your search yet. Try a different word — or browse the whole catalog.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {beats.map((b) => (
              <div key={b.id} className="rounded-2xl bg-[#0a0a0a] border border-[#1f1f1f] p-4 hover:border-emerald-400/40 transition group" data-testid={`store-beat-card-${b.id}`}>
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => previewToggle(b)}
                    className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center bg-emerald-500 hover:bg-emerald-400 text-black shadow-[0_0_18px_rgba(16,185,129,0.35)]"
                    data-testid={`store-preview-btn-${b.id}`}
                    aria-label={playingId === b.id ? "Pause preview" : "Play preview"}
                  >
                    {playingId === b.id ? <Pause size={18} weight="fill" /> : <Play size={18} weight="fill" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="font-mono font-bold text-lg truncate">"{b.beat_name}"</div>
                    <div className="text-xs text-white/50 truncate">{b.artist_tag}</div>
                    <div className="mt-2 flex items-center gap-3 text-[11px] text-white/60">
                      <span className="flex items-center gap-1"><MusicNote size={12} weight="bold" /> {b.bpm} BPM</span>
                      {b.musical_key ? <span>{b.musical_key}</span> : null}
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex items-end justify-between">
                  <div className="text-[11px] text-white/40">From <span className="text-emerald-300 font-bold">{priceFmt(b.prices?.mp3)}</span></div>
                  <button
                    onClick={() => nav(`/beats/${b.artist_slug}/${b.name_slug}`)}
                    className="text-xs font-bold px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-emerald-400/50 hover:text-emerald-300"
                    data-testid={`store-open-detail-${b.id}`}
                  >View →</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
